import React, { useState } from 'react';
import '../style/organizationLocation.css';

const OrganizationLocation = ({ locations, onLocationsChange }) => {
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [editingItem, setEditingItem] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  const toggleExpanded = (itemId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const addLocation = (parentId = null, level = 1) => {
 
    if (level > 4) {
      return;
    }


    const getLocationName = (level, parentId, locations) => {
      if (parentId) {

        const countChildren = (locations) => {
          let count = 0;
          locations.forEach(location => {
            if (location.parentId === parentId) {
              count++;
            }
            if (location.children && location.children.length > 0) {
              count += countChildren(location.children);
            }
          });
          return count;
        };
        
        const existingCount = countChildren(locations);
        return `Location Level ${level}`;
      } else {
       
        const existingCount = locations.filter(loc => loc.level === level).length;
        return `Location Level ${level}`;
      }
    };

    const newLocation = {
      id: Date.now().toString(),
      name: getLocationName(level, parentId, locations),
      level,
      parentId,
      children: []
    };

    if (parentId) {
      const updateLocations = (locations) => {
        return locations.map(location => {
          if (location.id === parentId) {
            return {
              ...location,
              children: [...location.children, newLocation]
            };
          }
          if (location.children && location.children.length > 0) {
            return {
              ...location,
              children: updateLocations(location.children)
            };
          }
          return location;
        });
      };
      onLocationsChange(updateLocations(locations));
    } else {
      onLocationsChange([...locations, newLocation]);
    }
  };

  const deleteLocation = (itemId) => {
    const updateLocations = (locations) => {
      return locations.filter(location => {
        if (location.id === itemId) {
          return false;
        }
        if (location.children && location.children.length > 0) {
          location.children = updateLocations(location.children);
        }
        return true;
      });
    };
    onLocationsChange(updateLocations(locations));
  };

  const startEditing = (item) => {
    setEditingItem(item.id);
    setEditingValue(item.name);
  };

  const saveEdit = () => {
    if (!editingValue.trim()) return;

    const updateLocations = (locations) => {
      return locations.map(location => {
        if (location.id === editingItem) {
          return { ...location, name: editingValue.trim() };
        }
        if (location.children && location.children.length > 0) {
          return {
            ...location,
            children: updateLocations(location.children)
          };
        }
        return location;
      });
    };
    onLocationsChange(updateLocations(locations));
    setEditingItem(null);
    setEditingValue('');
  };

  const cancelEdit = () => {
    setEditingItem(null);
    setEditingValue('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  const getLevelColor = (level) => {
    const colors = {
      1: '#006d7d',
      2: '#20b2aa',
      3: '#808080',
      4: '#a9a9a9'
    };
    return colors[level] || '#d3d3d3';
  };

  const renderLocationItem = (location, depth = 0) => {
    const hasChildren = location.children && location.children.length > 0;
    const isExpanded = expandedItems.has(location.id);
    const isEditing = editingItem === location.id;

    return (
      <div key={location.id} className="location-item">
        <div 
          className={`location-row ${hasChildren ? 'expandable' : ''}`}
          style={{ paddingLeft: `${depth * 20}px` }}
          onClick={hasChildren ? (e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleExpanded(location.id);
          } : undefined}
        >
          {hasChildren && (
            <div className="expand-icon">
              {isExpanded ? '▼' : '▶'}
            </div>
          )}
          {!hasChildren && <div className="expand-spacer" />}
          
          <div 
            className="level-indicator"
            style={{ backgroundColor: getLevelColor(location.level) }}
          />
          
          {isEditing ? (
            <input
              type="text"
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onBlur={saveEdit}
              onKeyDown={handleKeyPress}
              className="edit-input"
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span 
              className="location-name"
              onDoubleClick={(e) => {
                e.stopPropagation();
                startEditing(location);
              }}
            >
              {location.name}
            </span>
          )}
          
          <div className="location-actions">
            <button
              type="button"
              className="action-button delete-button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                deleteLocation(location.id);
              }}
              title="Delete"
            >
              ✕
            </button>
            {location.level < 4 && (
              <button
                type="button"
                className="action-button add-button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  addLocation(location.id, location.level + 1);
                }}
                title="Add sub-location"
              >
                +
              </button>
            )}
          </div>
        </div>
        
        {hasChildren && isExpanded && (
          <div className="location-children">
            {location.children.map(child => renderLocationItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const getMaxLevel = (locations) => {
    let maxLevel = 0;
    locations.forEach(location => {
      maxLevel = Math.max(maxLevel, location.level);
      if (location.children && location.children.length > 0) {
        maxLevel = Math.max(maxLevel, getMaxLevel(location.children));
      }
    });
    return maxLevel;
  };

  const maxLevel = getMaxLevel(locations);

  return (
    <div className="location-hierarchy">
      <div className="hierarchy-header">
        <h3>Location Hierarchy</h3>
        <div className="level-indicators">
          {[1, 2, 3, 4].map(level => (
            <div key={level} className="level-indicator-item">
              <div 
                className="level-dot"
                style={{ 
                  backgroundColor: level <= maxLevel ? getLevelColor(level) : '#e0e0e0',
                  border: level === 4 && level > maxLevel ? '2px solid #e0e0e0' : 'none'
                }}
              />
              <span className="level-text">Level {level}</span>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="add-level-button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            addLocation(null, 1);
          }}
        >
          + Add Level 1
        </button>
      </div>
      
      <div className="hierarchy-list">
        {locations.length === 0 ? (
          <div className="empty-state">
            No locations defined. Click "Add Level 1" to get started.
          </div>
        ) : (
          locations.map(location => renderLocationItem(location))
        )}
      </div>
    </div>
  );
};

export default OrganizationLocation;
